import { DSAR_ACTION_TO_TAKE_NAMES } from '/b2b/dsarActionItems/constants';
import { countryLookup } from '../common/helpers/Address';
import { gradients } from '/b2b/common/colors';

export const EVENTS = {
    ADD_FIELD: 'AddField',
    COPY_FORM: 'CopyForm',
    CREATE_FORM: 'CreateForm',
    DELETE_FORM: 'DeleteForm',
    GET_CODE_DSAR: 'GetCodeDSAR',
    GO_TO_FORM_DSAR: 'GoToFormDSAR',
    OPEN_DETAILS: 'OpenDetails',
    OPEN_FORM_FIELDS: 'OpenFormFields',
    OPEN_PROCESSING: 'OpenProcessing',
    OPEN_REQUEST_TYPES: 'OpenRequestTypes',
    OPEN_STYLING: 'OpenStyling',
    SAVE_FORM: 'SaveForm',
    SELECT_FIELD_TYPE: 'SelectFieldType',
};

export const DSAR_REQUEST_SOURCES = {
    ADMIN: 'ADMIN',
    AGENT_WEB: 'AGENT_WEB',
    API: 'API',
    CMP: 'CMP',
    CSV: 'CSV',
    EMAIL_INTAKE: 'EMAIL_INTAKE',
    TWILIO: 'TWILIO',
    WEB: 'WEB',
};

export const DSAR_REQUEST_SOURCE_LABELS = {
    [DSAR_REQUEST_SOURCES.ADMIN]: 'common.admin',
    [DSAR_REQUEST_SOURCES.AGENT_WEB]: 'common.agentWeb',
    [DSAR_REQUEST_SOURCES.API]: 'common.api',
    [DSAR_REQUEST_SOURCES.CMP]: 'common.cookieConsent',
    [DSAR_REQUEST_SOURCES.CSV]: 'common.csv',
    [DSAR_REQUEST_SOURCES.EMAIL_INTAKE]: 'common.email',
    [DSAR_REQUEST_SOURCES.TWILIO]: 'common.twilio',
    [DSAR_REQUEST_SOURCES.WEB]: 'common.webForm',
};

export const DSAR_REQUEST_TYPES = {
    DELETION: 'DELETE',
    CORRECTION: 'CORRECT',
    CUSTOM_1: 'CUSTOM_1',
    CUSTOM_2: 'CUSTOM_2',
    CUSTOM_3: 'CUSTOM_3',
    CUSTOM_4: 'CUSTOM_4',
    CUSTOM_5: 'CUSTOM_5',
    DO_NOT_SELL: 'DO_NOT_SELL',
    LIMIT_USE: 'LIMIT_USE',
    LIST_OR_CATEGORIES_OF_THIRD_PARTIES: 'LIST_OR_CATEGORIES_OF_THIRD_PARTIES',
    OPT_OUT_OF_USING_SENSITIVE_DATA: 'OPT_OUT_OF_USING_SENSITIVE_DATA',
    OPT_OUT: 'OPT_OUT',
    OPT_OUT_OF_PROFILING: 'OPT_OUT_OF_PROFILING',
    OTHER: 'OTHER',
    PORTABILITY: 'PORTABILITY',
    SUMMARIZE: 'SUMMARIZE',
};

export const DSAR_REQUEST_TYPE_GROUPS = {
    ACCESS: 'ACCESS',
    DELETE: 'DELETE',
    OPT_OUT: 'OPT_OUT',
    OPT_IN: 'OPT_IN',
    OTHER: 'OTHER',
};

export const DSAR_REQUEST_TYPE_LABELS = {
    [DSAR_REQUEST_TYPES.DELETION]: 'dsar.requestType.deletion',
    [DSAR_REQUEST_TYPES.CORRECTION]: 'dsar.requestType.correction',
    [DSAR_REQUEST_TYPES.CUSTOM_1]: 'dsar.requestType.custom1',
    [DSAR_REQUEST_TYPES.CUSTOM_2]: 'dsar.requestType.custom2',
    [DSAR_REQUEST_TYPES.CUSTOM_3]: 'dsar.requestType.custom3',
    [DSAR_REQUEST_TYPES.CUSTOM_4]: 'dsar.requestType.custom4',
    [DSAR_REQUEST_TYPES.CUSTOM_5]: 'dsar.requestType.custom5',
    [DSAR_REQUEST_TYPES.DO_NOT_SELL]: 'dsar.requestType.doNotSell',
    [DSAR_REQUEST_TYPES.LIMIT_USE]: 'dsar.requestType.limitUse',
    [DSAR_REQUEST_TYPES.LIST_OR_CATEGORIES_OF_THIRD_PARTIES]:
        'dash.requestType.LIST_OR_CATEGORIES_OF_THIRD_PARTIES.name',
    [DSAR_REQUEST_TYPES.OPT_OUT_OF_USING_SENSITIVE_DATA]:
        'dsar.requestType.optOutOfUsingSensitiveData',
    [DSAR_REQUEST_TYPES.OPT_OUT_OF_PROFILING]: 'dsar.requestType.optOutOfProfiling',
    [DSAR_REQUEST_TYPES.OPT_OUT]: 'dsar.requestType.optOut',
    [DSAR_REQUEST_TYPES.OTHER]: 'dsar.requestType.other',
    [DSAR_REQUEST_TYPES.PORTABILITY]: 'dsar.requestType.portability',
    [DSAR_REQUEST_TYPES.SUMMARIZE]: 'dsar.requestType.summarize',
};

export const DSAR_REQUEST_TYPE_GROUP_LABELS = {
    [DSAR_REQUEST_TYPE_GROUPS.ACCESS]: 'dsar.requestTypeGroup.access.title',
    [DSAR_REQUEST_TYPE_GROUPS.DELETE]: 'dsar.requestTypeGroup.delete.title',
    [DSAR_REQUEST_TYPE_GROUPS.OPT_OUT]: 'dsar.requestTypeGroup.optOut.title',
    [DSAR_REQUEST_TYPE_GROUPS.OPT_IN]: 'dsar.requestTypeGroup.optIn.title',
    [DSAR_REQUEST_TYPE_GROUPS.OTHER]: 'dsar.requestTypeGroup.other.title',
};

// The actions a workflow node's actionConfig can hold, with a name for each.
// DSAR_ACTION_TO_TAKE_NAMES omits NOT_APPLICABLE even though it is a valid stored
// value (see workflows.workflow_nodes), so fill that one in.
export const DSAR_ACTION_LABELS = {
    ...DSAR_ACTION_TO_TAKE_NAMES,
    NOT_APPLICABLE: 'dsar.requestActions.notApplicable',
};

export const DSAR_REQUEST_TYPE_LABELS_ENGLISH = {
    CORRECTION: 'Correct',
    CUSTOM: 'Custom',
    CUSTOM_1: 'Custom 1',
    CUSTOM_2: 'Custom 2',
    CUSTOM_3: 'Custom 3',
    CUSTOM_4: 'Custom 4',
    CUSTOM_5: 'Custom 5',
    DELETION: 'Delete',
    DO_NOT_SELL: 'Do Not Sell or Share to a Third Party',
    LIMIT_USE: 'Limit Use of Sensitive Data',
    OPT_OUT_OF_USING_SENSITIVE_DATA: 'Opt Out of Using Sensitive Data',
    OPT_OUT: 'Opt Out of Targeted Advertising',
    OPT_OUT_OF_PROFILING: 'Opt Out of Profiling / Automated Decision-Making',
    OTHER: 'Other',
    PORTABILITY: 'Portability',
    SUMMARIZE: 'Summarize',
};

// TODO: Remove constant once DSAR forms have internationalization
export const DSAR_REQUEST_TYPE_LABELS_TRANSLATIONS = {
    'dsar.requestType.correction': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CORRECTION,
    'dsar.requestType.custom': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM,
    'dsar.requestType.custom1': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM_1,
    'dsar.requestType.custom2': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM_2,
    'dsar.requestType.custom3': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM_3,
    'dsar.requestType.custom4': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM_4,
    'dsar.requestType.custom5': DSAR_REQUEST_TYPE_LABELS_ENGLISH.CUSTOM_5,
    'dsar.requestType.deletion': DSAR_REQUEST_TYPE_LABELS_ENGLISH.DELETION,
    'dsar.requestType.doNotSell': DSAR_REQUEST_TYPE_LABELS_ENGLISH.DO_NOT_SELL,
    'dsar.requestType.limitUse': DSAR_REQUEST_TYPE_LABELS_ENGLISH.LIMIT_USE,
    'dsar.requestType.optOutOfUsingSensitiveData':
        DSAR_REQUEST_TYPE_LABELS_ENGLISH.OPT_OUT_OF_USING_SENSITIVE_DATA,
    'dsar.requestType.optOut': DSAR_REQUEST_TYPE_LABELS_ENGLISH.OPT_OUT,
    'dsar.requestType.other': DSAR_REQUEST_TYPE_LABELS_ENGLISH.OTHER,
    'dsar.requestType.portability': DSAR_REQUEST_TYPE_LABELS_ENGLISH.PORTABILITY,
    'dsar.requestType.summarize': DSAR_REQUEST_TYPE_LABELS_ENGLISH.SUMMARIZE,
};

const DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES = {
    AUTHORIZED_AGENT: 'AUTHORIZED_AGENT',
    PARENT_OR_GUARDIAN: 'PARENT_OR_GUARDIAN',
    OTHER: 'OTHER',
};

export const DSAR_REPRESENTATIVE_RELATIONSHIP_TYPES = {
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.AUTHORIZED_AGENT]:
        'dsar.representativeRelationship.authorizedAgent',
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.PARENT_OR_GUARDIAN]:
        'dsar.representativeRelationship.parentOrGuardian',
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.OTHER]: 'dsar.representativeRelationship.other',
};

export const DSAR_REPRESENTATIVE_RELATIONSHIP_TYPES_ENGLISH = {
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.AUTHORIZED_AGENT]: 'Authorized Agent',
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.PARENT_OR_GUARDIAN]: 'Parent or Guardian',
    [DSAR_REPRESENTATIVE_RELATIONSHIP_VALUES.OTHER]: 'Other',
};

export const DSAR_REQUESTOR_TYPES = {
    EMPLOYEE: 'dsar.requestor.employee',
    CUSTOMER: 'dsar.requestor.subject',
    OTHER: 'dsar.requestor.other',
};

export const DSAR_REQUESTOR_TYPES_ENGLISH = {
    EMPLOYEE: 'Employee',
    CUSTOMER: 'Subject',
    OTHER: 'Other',
};

export const DSAR_FIELD_TYPES = {
    CHECKBOX: 'checkbox',
    DATEPICKER: 'datepicker',
    EMAIL: 'email',
    FILE: 'file',
    HIDDEN: 'hidden',
    MULTISELECT: 'multiselect',
    PHONE: 'phone',
    READ_ONLY: 'readonly',
    SELECT: 'select',
    TEXT: 'text',
    TEXTAREA: 'textarea',
};

export const DSAR_FIELD_TYPE_OPTIONS = [
    { value: DSAR_FIELD_TYPES.TEXT, label: 'dsarFieldTypes.textField' },
    { value: DSAR_FIELD_TYPES.READ_ONLY, label: 'dsarFieldTypes.readOnly' },
    { value: DSAR_FIELD_TYPES.EMAIL, label: 'dsarFieldTypes.emailTextField' },
    { value: DSAR_FIELD_TYPES.PHONE, label: 'dsarFieldTypes.phoneNumber' },
    { value: DSAR_FIELD_TYPES.TEXTAREA, label: 'dsarFieldTypes.multilineTextField' },
    { value: DSAR_FIELD_TYPES.SELECT, label: 'dsarFieldTypes.dropdown' },
    { value: DSAR_FIELD_TYPES.MULTISELECT, label: 'dsarFieldTypes.multiSelectDropdown' },
    { value: DSAR_FIELD_TYPES.CHECKBOX, label: 'dsarFieldTypes.checkbox' },
    { value: DSAR_FIELD_TYPES.DATEPICKER, label: 'dsarFieldTypes.datePicker' },
    { value: DSAR_FIELD_TYPES.HIDDEN, label: 'dsarFieldTypes.hiddenField' },
    { value: DSAR_FIELD_TYPES.FILE, label: 'dsarFieldTypes.fileUpload' },
];

export const DSAR_FONTS = [
    { value: 'Arial, Helvetica, sans-serif', label: 'Arial, Helvetica, sans-serif' },
    { value: 'Impact, Charcoal, sans-serif', label: 'Impact, Charcoal, sans-serif' },
    { value: 'Georgia, serif', label: 'Georgia, serif' },
    { value: '"Times New Roman", Times, serif', label: 'Times New Roman, Times, serif' },
    { value: '"Courier New", Courier, monospace', label: 'Courier New, Courier, monospace' },
    { value: '"Lucida Console", Monaco, monospace', label: 'Lucida Console, Monaco, monospace' },
];

export const DSAR_DISPLAY_OPTIONS = {
    DEFAULT: 'default',
    ENABLED: 'enabled',
    DISABLED: 'disabled',
};

export const COUNTRY_ID = 'country';
export const IDENTITY_VERIFY_ID = 'identification-file';
export const REQUEST_DESCRIPTION_ID = 'request-description';
export const TERRITORY_ID = 'territory';
export const REQUEST_TYPE = 'request-type';
export const PHONE_NUMBER = 'phone-number';

// these used to be actual form fields, but are now handled via other features
export const DSAR_LEGACY_FIELD_IDS = [COUNTRY_ID, TERRITORY_ID, REQUEST_TYPE];

export const DSAR_COUNTRY_LOOKUP = Object.values(countryLookup).reduce(
    (countries, { name, twoLetter }) => {
        countries[twoLetter] = {
            value: twoLetter,
            label: name,
        };
        return countries;
    },
    {}
);
export const MAX_ID_FILE_SIZE_MB = 8;
export const DISALLOWED_FILE_TYPES = ['image/svg+xml'];

export const DSAR_DETAILS_FIELD_IDS = {
    ATTACHMENTS: 'attachments',
    CITY: 'city',
    COUNTRY: 'country',
    COUNTRY_GEO: 'country-geo',
    COUNTRY_NAME: 'country-name',
    CREATED: 'created',
    CUSTOMER_ID: 'customer-id',
    DUE_DATE: 'due-date',
    DUE: 'due',
    EMAIL: 'email',
    FAMILY_NAME: 'family-name',
    FORM_ID: 'form-id',
    GIVEN_NAME: 'given-name',
    IDENTIFICATION_FILE: 'identification-file',
    IMAGE_EXTENSION: 'image-extension',
    VERIFIED_DATE: 'verified-date',
    INTERNAL_DUE_DATE: 'internal-due-date',
    LANGUAGE: 'language',
    ORIGIN: 'origin',
    PHONE_NUMBER: 'phone-number',
    PRIVACY_LAW: 'privacy-law',
    PRIVACY_LAW_DEADLINE: 'privacy-law-deadline',
    PRIVACY_LAW_STATUS: 'privacy-law-status',
    REPRESENTATIVE_AUTHORIZATION_FILE: 'representative-authorization-file',
    REPRESENTATIVE_DETAILS: 'representative-details',
    REPRESENTATIVE_EMAIL: 'representative-email',
    REPRESENTATIVE_FAMILY_NAME: 'representative-family-name',
    REPRESENTATIVE_GIVEN_NAME: 'representative-given-name',
    REPRESENTATIVE_RELATIONSHIP: 'representative-relationship',
    REPRESENTATIVE_TYPE: 'representative-type',
    REQUESTOR_TYPE: 'requestor-type',
    REQUEST_DESCRIPTION: 'request-description',
    EMAIL_INTAKE_SUBJECT: 'email-intake-subject',
    EMAIL_INTAKE_TEXT: 'email-intake-text',
    REQUEST_ID: 'request-id',
    REQUEST_SOURCE: 'request-source',
    REQUEST_TYPE: 'request-type',
    REQUEST_TYPE_DESCRIPTION: 'request-type-description',
    REQUEST_TYPE_ID: 'request-type-id',
    REQUEST_TYPE_INSTRUCTIONS: 'request-type-instructions',
    REQUEST_TYPE_TITLE: 'request-type-title',
    STATUS: 'status',
    TERRITORY: 'territory',
    TERRITORY_GEO: 'territory-geo',
    TERRITORY_NAME: 'territory-name',
    TWILIO: 'twilio',
    UPDATED: 'updated',
};

export const DSAR_STANDARD_FIELDS = {
    [DSAR_DETAILS_FIELD_IDS.EMAIL]: {
        title: 'dsar.fields.email.label',
        i18nLabel: 'dsar.fields.email.label',
        enLabel: 'Email',
        type: DSAR_FIELD_TYPES.EMAIL,
        required: true,
        editable: false,
        excludable: false,
        excluded: false,
    },
    [DSAR_DETAILS_FIELD_IDS.GIVEN_NAME]: {
        title: 'dsar.fields.firstName.label',
        i18nLabel: 'dsar.fields.firstName.label',
        enLabel: 'First Name',
        type: DSAR_FIELD_TYPES.TEXT,
        required: false,
        editable: true,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.FAMILY_NAME]: {
        title: 'dsar.fields.lastName.label',
        i18nLabel: 'dsar.fields.lastName.label',
        enLabel: 'Last Name',
        type: DSAR_FIELD_TYPES.TEXT,
        required: false,
        editable: true,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.PHONE_NUMBER]: {
        title: 'dsar.fields.phoneNumber.label',
        i18nLabel: 'dsar.fields.phoneNumber.label',
        enLabel: 'Phone Number',
        type: DSAR_FIELD_TYPES.PHONE,
        required: false,
        editable: true,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.IDENTIFICATION_FILE]: {
        title: 'dsar.fields.proofOfIdentity.label',
        i18nLabel: 'dsar.fields.proofOfIdentity.label',
        enLabel: 'Proof of Identity',
        type: DSAR_FIELD_TYPES.FILE,
        required: true,
        editable: false,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.REQUESTOR_TYPE]: {
        title: 'dsar.fields.requestorType.label',
        i18nLabel: 'dsar.fields.requestorType.label',
        enLabel: 'Requestor Type',
        type: DSAR_FIELD_TYPES.SELECT,
        options: Object.entries(DSAR_REQUESTOR_TYPES_ENGLISH).map(([value, label]) => ({
            value,
            label,
        })),
        required: false,
        editable: true,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_DESCRIPTION]: {
        title: 'dsar.fields.requestDescription.label',
        i18nLabel: 'dsar.fields.requestDescription.label',
        enLabel: 'Request Description',
        type: DSAR_FIELD_TYPES.TEXTAREA,
        required: false,
        editable: true,
        excludable: true,
        excluded: true,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE]: {
        title: 'dsar.fields.representativeType.title',
        i18nLabel: 'dsar.fields.representativeType.label',
        enLabel: 'I am submitting this request on behalf of someone other than myself.',
        type: DSAR_FIELD_TYPES.CHECKBOX,
        required: false,
        editable: false,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_RELATIONSHIP]: {
        title: 'dsar.fields.representativeRelationship.label',
        i18nLabel: 'dsar.fields.representativeRelationship.label',
        enLabel: 'Representative Relationship',
        type: DSAR_FIELD_TYPES.SELECT,
        options: Object.entries(DSAR_REPRESENTATIVE_RELATIONSHIP_TYPES_ENGLISH).map(
            ([value, label]) => ({ value, label })
        ),
        required: true,
        editable: false,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_EMAIL]: {
        title: 'dsar.fields.representativeEmail.label',
        i18nLabel: 'dsar.fields.representativeEmail.label',
        enLabel: 'Representative Email',
        type: DSAR_FIELD_TYPES.EMAIL,
        required: true,
        editable: false,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_GIVEN_NAME]: {
        title: 'dsar.fields.representativeGivenName.label',
        i18nLabel: 'dsar.fields.representativeGivenName.label',
        enLabel: 'Representative First Name',
        type: DSAR_FIELD_TYPES.TEXT,
        required: true,
        editable: true,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_FAMILY_NAME]: {
        title: 'dsar.fields.representativeFamilyName.label',
        i18nLabel: 'dsar.fields.representativeFamilyName.label',
        enLabel: 'Representative Last Name',
        type: DSAR_FIELD_TYPES.TEXT,
        required: true,
        editable: true,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_AUTHORIZATION_FILE]: {
        title: 'dsar.fields.representativeAuthorizationFile.label',
        i18nLabel: 'dsar.fields.representativeAuthorizationFile.label',
        enLabel: 'Representative Authorization File',
        type: DSAR_FIELD_TYPES.FILE,
        required: true,
        editable: true,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_DETAILS]: {
        title: 'dsar.fields.representativeDetails.label',
        i18nLabel: 'dsar.fields.representativeDetails.label',
        enLabel: 'Representative Details',
        type: DSAR_FIELD_TYPES.TEXTAREA,
        required: false,
        editable: true,
        excludable: true,
        excluded: false,
        displayOption: DSAR_DISPLAY_OPTIONS.DISABLED,
        conditional: {
            id: DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_TYPE,
            value: true,
        },
    },
};

export const DSAR_STANDARD_FIELDS_EN = Object.keys(DSAR_STANDARD_FIELDS).reduce((acc, curr) => {
    const { i18nLabel, enLabel, ...rest } = DSAR_STANDARD_FIELDS[curr];
    acc[curr] = { ...rest, label: enLabel };
    return acc;
}, {});

export const DSAR_STANDARD_FIELDS_I18N = Object.keys(DSAR_STANDARD_FIELDS).reduce((acc, curr) => {
    const { i18nLabel, enLabel, ...rest } = DSAR_STANDARD_FIELDS[curr];
    acc[curr] = { ...rest, label: i18nLabel };
    return acc;
}, {});

export const DSAR_INFO_FIELDS = {
    [DSAR_DETAILS_FIELD_IDS.ATTACHMENTS]: {
        label: 'common.attachments',
    },
    [DSAR_DETAILS_FIELD_IDS.COUNTRY]: {
        label: 'dsar.fields.userSelectedLocation.label',
        helperText: 'dsar.fields.userSelectedLocation.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.COUNTRY_GEO]: {
        label: 'dsar.fields.systemIdentifiedLocation.label',
        helperText: 'dsar.fields.systemIdentifiedLocation.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.CREATED]: {
        label: 'common.created',
        helperText: 'dsar.fields.created.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.DUE_DATE]: {
        label: 'dsar.fields.regulatoryDueDate.label',
        helperText: 'dsar.fields.regulatoryDueDate.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.EMAIL]: {
        label: 'dsar.fields.email.label',
        helperText: 'dsar.fields.email.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.FAMILY_NAME]: {
        label: 'dsar.fields.lastName.label',
        helperText: 'dsar.fields.lastName.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.FORM_ID]: {
        label: 'dsar.fields.form.label',
        helperText: 'dsar.fields.form.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.GIVEN_NAME]: {
        label: 'dsar.fields.firstName.label',
        helperText: 'dsar.fields.firstName.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.VERIFIED_DATE]: {
        label: 'dsar.fields.verifiedDate.label',
        helperText: 'dsar.fields.verifiedDate.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.INTERNAL_DUE_DATE]: {
        label: 'dsar.fields.dueDate.label',
        helperText: 'dsar.fields.dueDate.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.LANGUAGE]: {
        label: 'dsar.fields.language.label',
        helperText: 'dsar.fields.language.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.PHONE_NUMBER]: {
        label: 'dsar.fields.phoneNumber.label',
        helperText: 'dsar.fields.phoneNumber.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.PRIVACY_LAW]: {
        label: 'dsar.fields.privacyLaw.label',
        helperText: 'dsar.fields.privacyLaw.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.PRIVACY_LAW_DEADLINE]: {
        label: 'dsar.fields.privacyLawDeadline.label',
        helperText: 'dsar.fields.privacyLawDeadline.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_EMAIL]: {
        label: 'dsar.fields.representativeEmail.label',
        helperText: 'dsar.fields.representativeEmail.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_DETAILS]: {
        label: 'dsar.fields.representativeDetails.label',
        helperText: 'dsar.fields.representativeDetails.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_FAMILY_NAME]: {
        label: 'dsar.fields.representativeFamilyName.label',
        helperText: 'dsar.fields.representativeFamilyName.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_GIVEN_NAME]: {
        label: 'dsar.fields.representativeGivenName.label',
        helperText: 'dsar.fields.representativeGivenName.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REPRESENTATIVE_RELATIONSHIP]: {
        label: 'dsar.fields.representativeRelationship.label',
        helperText: 'dsar.fields.representativeRelationship.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUESTOR_TYPE]: {
        label: 'dsar.fields.requestorType.label',
        helperText: 'dsar.fields.requestorType.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_DESCRIPTION]: {
        label: 'dsar.fields.requestDescription.label',
        helperText: 'dsar.fields.requestDescription.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.EMAIL_INTAKE_SUBJECT]: {
        label: 'dsar.fields.emailIntakeSubject.label',
        helperText: 'dsar.fields.emailIntakeSubject.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.EMAIL_INTAKE_TEXT]: {
        label: 'dsar.fields.emailIntakeText.label',
        helperText: 'dsar.fields.emailIntakeText.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_ID]: {
        label: 'dsar.fields.requestId.label',
        helperText: 'dsar.fields.requestId.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_SOURCE]: {
        label: 'dsar.fields.requestSource.label',
        helperText: 'dsar.fields.requestSource.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_TYPE]: {
        label: 'dsar.fields.requestType.label',
        helperText: 'dsar.fields.requestType.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_TYPE_DESCRIPTION]: {
        label: 'dsar.fields.requestTypeDescription.label',
        helperText: 'dsar.fields.requestTypeDescription.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_TYPE_INSTRUCTIONS]: {
        label: 'dsar.fields.requestTypeInstructions.label',
        helperText: 'dsar.fields.requestTypeInstructions.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.REQUEST_TYPE_TITLE]: {
        label: 'dsar.fields.requestTypeTitle.label',
        helperText: 'dsar.fields.requestTypeTitle.helperText',
    },
    [DSAR_DETAILS_FIELD_IDS.UPDATED]: {
        label: 'common.updated',
    },
};

export const DSAR_TEMPLATE_TYPE_EMAIL = 'EMAIL';
export const DSAR_TEMPLATE_TYPE_WEB = 'WEB';
export const DSAR_TEMPLATE_TYPE_PORTAL = 'PORTAL';

export const DSAR_TEMPLATE_SUBTYPE_COMPLETION = 'COMPLETION';

export const DSAR_TEMPLATE_SUBTYPE_REJECTION = 'REJECTION';

export const DSAR_TEMPLATE_SUBTYPE_PORTAL = 'PORTAL_MESSAGE';

export const DSAR_TEMPLATE_TYPES = {
    [DSAR_TEMPLATE_TYPE_EMAIL]: 'dsar.templates.types.email', // "Email"
    [DSAR_TEMPLATE_TYPE_WEB]: 'dsar.templates.types.web', // "Web"
    [DSAR_TEMPLATE_TYPE_PORTAL]: 'dsar.templates.types.portal', // "Portal"
};

export const DSAR_RICH_TEXT_TOOLBAR_OPTIONS = [
    ['heading', 'bold', 'italic', 'strike', 'quote', 'hr'],
    ['ul', 'ol', 'indent', 'outdent'],
    ['table', 'link'],
];

export const DSAR_REQUEST_TYPE_JURISDICTIONS = {
    DEFAULT: 'default',
    ENABLED: 'enabled',
    DISABLED: 'disabled',
};

export const DSAR_REQUEST_TYPE_VERIFICATION_LEVELS = {
    EMAIL_ONLY: 'EMAIL_ONLY',
    EMAIL_AND_IDENTITY: 'EMAIL_AND_IDENTITY',
    NONE: 'NONE',
};

export const DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVELS = {
    IDENTITY_ONLY: 'IDENTITY_ONLY',
    EMAIL_AND_IDENTITY: 'EMAIL_AND_IDENTITY',
    NONE: 'NONE',
};

export const DSAR_PRIVACY_LAW_STATUSES = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};

export const REQUEST_TYPE_COLOR_MAPPINGS = {
    [DSAR_REQUEST_TYPES.DELETION]: gradients.error,
    [DSAR_REQUEST_TYPES.OTHER]: gradients.warning,
    [DSAR_REQUEST_TYPES.CUSTOM_1]: gradients.warning,
    [DSAR_REQUEST_TYPES.CUSTOM_2]: gradients.warning,
    [DSAR_REQUEST_TYPES.CUSTOM_3]: gradients.warning,
    [DSAR_REQUEST_TYPES.CUSTOM_4]: gradients.warning,
    [DSAR_REQUEST_TYPES.CUSTOM_5]: gradients.warning,
    [DSAR_REQUEST_TYPES.PORTABILITY]: gradients.violet,
    [DSAR_REQUEST_TYPES.SUMMARIZE]: gradients.info,
    [DSAR_REQUEST_TYPES.CORRECTION]: gradients.success,
    [DSAR_REQUEST_TYPES.DO_NOT_SELL]: gradients.success,
    [DSAR_REQUEST_TYPES.LIMIT_USE]: gradients.success,
    [DSAR_REQUEST_TYPES.LIST_OR_CATEGORIES_OF_THIRD_PARTIES]: gradients.info,
    [DSAR_REQUEST_TYPES.OPT_OUT_OF_USING_SENSITIVE_DATA]: gradients.success,
    [DSAR_REQUEST_TYPES.OPT_OUT_OF_PROFILING]: gradients.success,
    [DSAR_REQUEST_TYPES.OPT_OUT]: gradients.success,
};

export const REQUEST_TYPE_GROUP_COLOR_MAPPINGS = {
    [DSAR_REQUEST_TYPE_GROUPS.ACCESS]: gradients.success,
    [DSAR_REQUEST_TYPE_GROUPS.DELETE]: gradients.error,
    [DSAR_REQUEST_TYPE_GROUPS.OPT_OUT]: gradients.warning,
    [DSAR_REQUEST_TYPE_GROUPS.OPT_IN]: gradients.violet,
    [DSAR_REQUEST_TYPE_GROUPS.OTHER]: gradients.info,
};
